'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormState, useFormStatus } from 'react-dom';
import {
  BUDGET_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  TIMELINE_OPTIONS,
} from '@/lib/services';
import { submitContactAction } from '@/app/contact/actions';
import { initialContactState, type ContactFormState } from '@/app/contact/form-state';
import { TurnstileWidget } from './Turnstile';
import { SendRipple } from '@/lib/sand/SendRipple';

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-elevated)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '14px 16px',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono), ui-monospace, monospace',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.5,
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: 8,
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono), ui-monospace, monospace',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
};

export function ContactForm() {
  const params = useSearchParams();
  const preselectedService = params.get('service') ?? '';
  const initialProjectType = useMemo(() => {
    const match = PROJECT_TYPE_OPTIONS.find((o) => o.value === preselectedService);
    return match?.value ?? '';
  }, [preselectedService]);

  const [state, formAction] = useFormState<ContactFormState, FormData>(
    submitContactAction,
    initialContactState
  );

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const submitRectRef = useRef<DOMRect | null>(null);
  const [sendRippleKey, setSendRippleKey] = useState(0);
  // Capture the Send button's bbox right before submit so we can use it
  // for the footprint-arrival stamp after the success state replaces the
  // button with the confirmation panel.
  useEffect(() => {
    if (state.status === 'success') {
      headingRef.current?.focus();
      setSendRippleKey((k) => k + 1);
      const r = submitRectRef.current;
      if (r && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('sand:arrival', {
            detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
          }),
        );
      }
    } else if (state.status === 'error') {
      const firstInvalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      firstInvalid?.focus();
    }
  }, [state]);

  if (state.status === 'success') {
    return (
      <div
        className="flex flex-col items-start gap-3 p-8"
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--accent-mid)',
          borderRadius: 'var(--radius-xl)',
        }}
        role="status"
        aria-live="polite"
      >
        <SendRipple triggerKey={sendRippleKey} />
        <span
          className="inline-flex items-center gap-2 font-mono"
          style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em' }}
        >
          <CheckCircle />
          MESSAGE SENT
        </span>
        <h3
          ref={headingRef}
          tabIndex={-1}
          className="font-display italic text-text"
          style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', outline: 'none' }}
        >
          Got it, thanks{state.submittedName ? `, ${state.submittedName.split(' ')[0]}` : ''}.
        </h3>
        <p className="text-text-secondary" style={{ fontSize: 14, lineHeight: 1.7 }}>
          I&apos;ll reply within 24 hours. Check your inbox (and spam folder) for a confirmation
          email.
        </p>
      </div>
    );
  }

  const fieldErrors = state.fieldErrors ?? {};

  return (
    <form action={formAction} className="flex flex-col gap-5" noValidate>
      {/* Honeypot */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
      >
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" autoComplete="off" tabIndex={-1} defaultValue="" />
      </div>

      <Row>
        <Field label="Name" htmlFor="name" error={fieldErrors.name}>
          <input
            id="name"
            name="name"
            type="text"
            className="contact-field"
            style={{ ...FIELD_STYLE, borderColor: fieldErrors.name ? 'var(--rose)' : FIELD_STYLE.borderColor }}
            placeholder="Jane Founder"
            required
            maxLength={200}
            autoComplete="name"
            aria-invalid={fieldErrors.name ? 'true' : undefined}
            aria-describedby={fieldErrors.name ? 'name-error' : undefined}
          />
        </Field>
        <Field label="Email" htmlFor="email" error={fieldErrors.email}>
          <input
            id="email"
            name="email"
            type="email"
            className="contact-field"
            style={{ ...FIELD_STYLE, borderColor: fieldErrors.email ? 'var(--rose)' : FIELD_STYLE.borderColor }}
            placeholder="jane@example.com"
            required
            maxLength={320}
            autoComplete="email"
            aria-invalid={fieldErrors.email ? 'true' : undefined}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
          />
        </Field>
      </Row>

      <Field label="Message" htmlFor="message" error={fieldErrors.message}>
        <textarea
          id="message"
          name="message"
          rows={6}
          className="contact-field"
          style={{
            ...FIELD_STYLE,
            resize: 'vertical',
            minHeight: 140,
            borderColor: fieldErrors.message ? 'var(--rose)' : FIELD_STYLE.borderColor,
          }}
          placeholder="Tell me about your project: what you're building, for whom, and where it is today."
          required
          minLength={20}
          maxLength={5000}
          aria-invalid={fieldErrors.message ? 'true' : undefined}
          aria-describedby={fieldErrors.message ? 'message-error' : undefined}
        />
      </Field>

      <details
        className="contact-details"
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-elevated)',
          padding: '12px 16px',
        }}
      >
        <summary
          className="font-mono"
          style={{
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Add project details (optional)
        </summary>
        <div className="mt-4 flex flex-col gap-5">
          <Row>
            <Field label="Project Type" htmlFor="projectType">
              <select
                id="projectType"
                name="projectType"
                className="contact-field"
                style={FIELD_STYLE}
                defaultValue={initialProjectType}
              >
                <option value="">Select one…</option>
                {PROJECT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Budget" htmlFor="budget">
              <select id="budget" name="budget" className="contact-field" style={FIELD_STYLE} defaultValue="">
                <option value="">Select one…</option>
                {BUDGET_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Field label="Timeline" htmlFor="timeline">
            <select id="timeline" name="timeline" className="contact-field" style={FIELD_STYLE} defaultValue="">
              <option value="">Select one…</option>
              {TIMELINE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </details>

      <TurnstileWidget />

      {state.error && state.status === 'error' && !state.fieldErrors && (
        <p className="font-mono" role="alert" style={{ color: 'var(--rose)', fontSize: 12 }}>
          {state.error}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <SubmitButton
          onPressCapture={(rect) => {
            submitRectRef.current = rect;
          }}
        />
        <p
          className="font-mono text-text-secondary"
          style={{ fontSize: 11, letterSpacing: '0.02em', lineHeight: 1.6 }}
        >
          I read every message. No newsletter, no spam, no auto-responders.
        </p>
      </div>
    </form>
  );
}

function SubmitButton({ onPressCapture }: { onPressCapture?: (rect: DOMRect) => void }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      onPointerDown={(e) => {
        onPressCapture?.(e.currentTarget.getBoundingClientRect());
      }}
      className="cta-primary contact-submit inline-flex items-center justify-center transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
      style={{
        fontSize: 15,
        fontWeight: 800,
        padding: '15px 32px',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      {pending ? (
        <span className="inline-flex items-center gap-2">
          <Spinner /> Sending…
        </span>
      ) : (
        'Send Message'
      )}
    </button>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>;
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} style={LABEL_STYLE}>
        {label}
      </label>
      {children}
      {error && (
        <p id={`${htmlFor}-error`} className="mt-1.5 font-mono" role="alert" style={{ color: 'var(--rose)', fontSize: 11 }}>
          {error}
        </p>
      )}
    </div>
  );
}

function CheckCircle() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
      style={{ animation: 'spin 1s linear infinite' }}
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.55" strokeLinecap="round" />
    </svg>
  );
}
